/**
 * 权限管理 · 🗑️ 回收站（A6·tech_admin only）
 * iOS 相册式：列出 30 天内被软删的业务记录 · 按类型分组 · 一键恢复 · 显剩余天数。
 * 后端契约：services/trash.ts（GET /admin/trash · restore · purge · tech_admin only）。
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Trash2, RotateCcw, Clock, ShieldAlert, AlertTriangle, PackageOpen, CheckCircle2,
} from "lucide-react";
import { trashApi, type TrashItem } from "@/services/trash";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { useCan } from "@/auth/permissions";
import { RbacTabs } from "./RbacTabs";

/** entity_type → emoji（分组头展示·后端已给中文 entity_label） */
const ENTITY_EMOJI: Record<string, string> = {
  order: "🧾", product: "🗺️", driver: "🧑‍✈️", vehicle: "🚐",
  hotel: "🏨", attraction: "🎟️", supplier: "🏢", channel_partner: "🤝",
  user: "👤",   // USERDEL-1 · 系统账号（恢复后仍是「已停用」，需在用户管理里启用）
};
const emojiOf = (t: string) => ENTITY_EMOJI[t] ?? "📦";

export function RecycleBinPage() {
  const isTechAdmin = useCan("system.trash.manage");

  const qc = useQueryClient();
  const [flash, setFlash] = useState<string | null>(null);
  const [showPurge, setShowPurge] = useState(false);

  const trashQ = useQuery({
    queryKey: ["rbac", "trash"],
    queryFn: () => trashApi.list(),
    enabled: isTechAdmin, // 非 tech_admin 不发请求（避免 403 噪音·下面渲染无权页）
  });

  const items = useMemo(() => trashQ.data ?? [], [trashQ.data]);

  // 按 entity_type 分组（保留后端倒序）
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; rows: TrashItem[] }>();
    for (const it of items) {
      const g = map.get(it.entity_type) ?? { label: it.entity_label, rows: [] };
      g.rows.push(it);
      map.set(it.entity_type, g);
    }
    return [...map.entries()].map(([type, g]) => ({ type, ...g }));
  }, [items]);

  const expiringSoon = items.filter((i) => i.days_left <= 7).length;

  const restoreMut = useMutation({
    mutationFn: (it: TrashItem) => trashApi.restore(it.entity_type, it.id),
    onSuccess: (_res, it) => {
      setFlash(`已恢复 ${it.entity_label}「${it.summary}」`);
      qc.invalidateQueries({ queryKey: ["rbac", "trash"] });
    },
  });

  const purgeMut = useMutation({
    mutationFn: () => trashApi.purge(),
    onSuccess: (res) => {
      setShowPurge(false);
      setFlash(`已清空到期记录 ${res.purged} 条`);
      qc.invalidateQueries({ queryKey: ["rbac", "trash"] });
    },
  });

  const onRestore = (it: TrashItem) => {
    if (restoreMut.isPending) return;
    if (window.confirm(`确认恢复 ${it.entity_label}「${it.summary}」？\n恢复后该记录将重新出现在对应模块。`)) {
      restoreMut.mutate(it);
    }
  };

  // 前端隐藏 + 直接访问也挡（后端 403 双保险）
  if (!isTechAdmin) {
    return (
      <PageShell title="回收站" subtitle="超级管理员专属">
        <RbacTabs />
        <div className="flex flex-col items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-6 py-16 text-center">
          <ShieldAlert className="h-7 w-7 text-amber-500" />
          <p className="text-[14px] font-semibold text-amber-800">仅超级管理员（技术管理员）可访问回收站</p>
          <p className="text-[12px] text-amber-700">数据恢复职责分离 · 老板等其它角色无权限</p>
        </div>
      </PageShell>
    );
  }

  if (trashQ.isLoading) return <LoadingState />;
  if (trashQ.isError) return <ErrorState message="回收站加载失败" onRetry={() => trashQ.refetch()} />;

  return (
    <PageShell title="回收站" subtitle="30 天内被删除的业务记录 · 可一键恢复 · 到期（>30 天）自动清除">
      <RbacTabs />

      {flash && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> {flash}
          <button onClick={() => setFlash(null)} className="ml-auto text-emerald-500 hover:text-emerald-700">关闭</button>
        </div>
      )}

      {/* StatRow */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <RStat icon={<Trash2 className="h-4 w-4" />} tone="slate" label="待恢复记录" value={`${items.length} 条`} hint={`${groups.length} 类业务`} />
        <RStat icon={<Clock className="h-4 w-4" />} tone="amber" label="即将到期（≤7 天）" value={`${expiringSoon} 条`} hint="过期后将被自动清除" />
        <RStat icon={<ShieldAlert className="h-4 w-4" />} tone="indigo" label="保留期" value="30 天" hint="软删后 30 天内可恢复" />
      </div>

      {/* 操作条 */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[12px] text-slate-500">恢复后记录会重新出现在对应模块；到期记录可手动清空释放空间。</p>
        <button
          type="button"
          onClick={() => setShowPurge(true)}
          className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-[12px] font-medium text-rose-600 hover:bg-rose-50"
        >
          <Trash2 className="h-3.5 w-3.5" /> 清空到期（&gt;30 天）
        </button>
      </div>

      {/* 分组列表 */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-16 text-center">
          <PackageOpen className="h-8 w-8 text-slate-300" />
          <p className="text-[14px] font-medium text-slate-500">回收站为空</p>
          <p className="text-[12px] text-slate-400">最近 30 天没有被删除的业务记录</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((g) => (
            <div key={g.type} className="rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
                <span className="text-lg">{emojiOf(g.type)}</span>
                <p className="text-[13px] font-semibold text-slate-800">{g.label}</p>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{g.rows.length}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {g.rows.map((it) => (
                  <li key={`${it.entity_type}:${it.id}`} className="flex items-center gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-slate-900">{it.summary}</p>
                      <p className="text-[11px] text-slate-400">删除于 {new Date(it.deleted_at).toLocaleString("zh-CN")}</p>
                    </div>
                    <DaysLeftBadge days={it.days_left} />
                    <button
                      type="button"
                      onClick={() => onRestore(it)}
                      disabled={restoreMut.isPending}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> 恢复
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* 清空到期·二次确认弹窗 */}
      {showPurge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl animate-in fade-in zoom-in-95">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-500" />
              <h3 className="text-[15px] font-semibold text-slate-900">清空到期记录</h3>
            </div>
            <p className="mt-3 text-[13px] text-slate-600">
              将<span className="font-semibold text-rose-600">永久删除</span>所有已超过 30 天保留期的记录，此操作<span className="font-semibold">不可恢复</span>。
              未到期（≤30 天）的记录不受影响。
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowPurge(false)}
                disabled={purgeMut.isPending}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => purgeMut.mutate()}
                disabled={purgeMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-rose-700 disabled:opacity-60"
              >
                <Trash2 className="h-3.5 w-3.5" /> {purgeMut.isPending ? "清空中…" : "确认清空"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function DaysLeftBadge({ days }: { days: number }) {
  const urgent = days <= 7;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
        urgent ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"
      }`}
      title="距自动清除剩余天数"
    >
      <Clock className="h-3 w-3" />
      {days <= 0 ? "今日到期" : `剩 ${days} 天`}
    </span>
  );
}

function RStat({ icon, tone, label, value, hint }: { icon: React.ReactNode; tone: "indigo" | "amber" | "slate"; label: string; value: string; hint: string }) {
  const cls: Record<string, string> = {
    indigo: "bg-indigo-100 text-indigo-600",
    amber: "bg-amber-100 text-amber-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${cls[tone]}`}>{icon}</span>
        <span className="text-[12px] text-slate-500">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      <p className="truncate text-[11px] text-slate-400" title={hint}>{hint}</p>
    </div>
  );
}
